import React, { forwardRef, useImperativeHandle, useState } from 'react';

import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import TextField from '@material-ui/core/TextField';
import CloseIcon from '@material-ui/icons/Close';

const MIN_COPIAS = 1;

const normalizarCopias = (valor) => {
  const numero = Math.floor(Number(valor));
  return Number.isFinite(numero) && numero >= MIN_COPIAS ? numero : MIN_COPIAS;
};

/**
 * Passo intermediário entre "selecionar produtos" e "gerar o PDF": lista os produtos
 * escolhidos e deixa o usuário definir o número de cópias (páginas A4) de cada cartaz.
 * Unifica os dois caminhos da tela — ação individual (1 produto) e ação em lote (N).
 *
 * Uso: `ref.current.handleOpen(produtos)`; ao confirmar chama
 * `onConfirmar(produtos com a chave `copias` preenchida)`.
 */
const DialogoSelecionarCopias = forwardRef(
  ({ onConfirmar = () => {}, onCancelar = () => {} }, ref) => {
    const [aberto, setAberto] = useState(false);
    const [produtos, setProdutos] = useState([]);
    const [copiasPorProduto, setCopiasPorProduto] = useState({});

    const limpar = () => {
      setAberto(false);
      setProdutos([]);
      setCopiasPorProduto({});
    };

    useImperativeHandle(ref, () => ({
      handleOpen(listaProdutos) {
        setProdutos(listaProdutos);
        setCopiasPorProduto(
          listaProdutos.reduce(
            (acc, produto) => ({ ...acc, [produto.id]: MIN_COPIAS }),
            {},
          ),
        );
        setAberto(true);
      },
    }));

    const cancelar = () => {
      limpar();
      onCancelar();
    };

    const confirmar = () => {
      const produtosComCopias = produtos.map((produto) => ({
        ...produto,
        copias: normalizarCopias(copiasPorProduto[produto.id]),
      }));
      limpar();
      onConfirmar(produtosComCopias);
    };

    const alterarCopias = (id, valor) =>
      setCopiasPorProduto((atual) => ({ ...atual, [id]: valor }));

    return (
      <Dialog open={aberto} onClose={cancelar} maxWidth="sm" fullWidth>
        <DialogTitle style={{ backgroundColor: '#009FD4', color: 'white' }}>
          Definir cópias por cartaz
          <IconButton
            aria-label="fechar"
            onClick={cancelar}
            style={{ position: 'absolute', right: 8, top: 8, color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers style={{ padding: 0 }}>
          <List disablePadding>
            {produtos.map((produto) => (
              <React.Fragment key={produto.id}>
                <ListItem>
                  <ListItemText
                    primary={produto.nome}
                    secondary={(produto.descricao_no_cartaz || '').replace(
                      /\n/g,
                      ' / ',
                    )}
                  />
                  <ListItemSecondaryAction>
                    <TextField
                      type="number"
                      label="Cópias"
                      size="small"
                      value={copiasPorProduto[produto.id] ?? MIN_COPIAS}
                      onChange={(e) =>
                        alterarCopias(produto.id, e.target.value)
                      }
                      onBlur={(e) =>
                        alterarCopias(
                          produto.id,
                          normalizarCopias(e.target.value),
                        )
                      }
                      inputProps={{ min: MIN_COPIAS, style: { width: 64 } }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelar}>Cancelar</Button>
          <Button
            onClick={confirmar}
            variant="contained"
            color="primary"
            disabled={!produtos.length}
          >
            Gerar
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

export default DialogoSelecionarCopias;
